import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { DgNumberPipe } from '../../dg-number.pipe';
import { DocumentGenerateConfigService } from '../../document-generate-config.service';
import { WarehouseAllocation } from '../../interfaces/document-item.interface';
import { WarehouseDistributionDialogData } from '../../interfaces/dialog-data.interface';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';

// margen para que un residuo de coma flotante no se lea como cantidad pendiente
const EPSILON = 0.000001;

interface DistributionRow {
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  available: number;
  quantity: number;
  quantityText: string;
  // fila del faltante: se surte del almacén principal sin respaldo de existencia
  withoutStock: boolean;
}

// Reparto de la cantidad de una línea entre los almacenes de la oficina de venta
@Component({
  selector: 'app-warehouse-distribution-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    DgNumberPipe,
  ],
  templateUrl: './warehouse-distribution-modal.component.html',
})
export class WarehouseDistributionModalComponent implements OnInit, OnDestroy {
  rows: DistributionRow[] = [];

  // en pantallas de poca altura (móvil horizontal) el resumen no deja ver los almacenes
  shortScreen = false;
  private shortScreenSub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<WarehouseDistributionModalComponent>,
    private dgConfig: DocumentGenerateConfigService,
    private breakpointObserver: BreakpointObserver,
    @Inject(MAT_DIALOG_DATA) public data: WarehouseDistributionDialogData,
  ) {
    // en consulta las filas son el reparto vigente; en edición, los almacenes disponibles
    if (data.readonly) {
      this.rows = (data.allocations ?? []).map((a) => ({
        warehouseId: a.warehouseId,
        warehouseCode: a.warehouseCode ?? '',
        warehouseName: a.warehouseName ?? '',
        available: 0,
        quantity: a.quantity || 0,
        quantityText: this.format(a.quantity || 0),
        withoutStock: a.withoutStock === true,
      }));
      return;
    }
    this.rows = (data.warehouses ?? []).map((w) => {
      const assigned = (data.allocations ?? []).find(
        (a) => a.warehouseId === w.warehouseId && !a.withoutStock,
      );
      const quantity = assigned?.quantity ?? 0;
      return {
        warehouseId: w.warehouseId,
        warehouseCode: w.warehouseCode ?? '',
        warehouseName: w.warehouseName ?? '',
        // el disponible se acota a los decimales que se pueden asignar: el residuo
        // que sobra no es asignable y se leía como existencia restante, lo que
        // impedía crear la fila de lo que no tiene respaldo
        available: this.round(w.available),
        quantity,
        quantityText: quantity ? this.format(quantity) : '',
        withoutStock: false,
      };
    });
    if (this.data.allowsWithoutStock) this.syncWithoutStockRow();
  }

  ngOnInit(): void {
    this.shortScreenSub = this.breakpointObserver
      .observe('(max-height: 560px)')
      .subscribe((state) => (this.shortScreen = state.matches));
  }

  ngOnDestroy(): void {
    this.shortScreenSub?.unsubscribe();
  }

  get isReadonly(): boolean {
    return this.data.readonly === true;
  }

  get assigned(): number {
    return this.rows.reduce((sum, r) => sum + (r.quantity || 0), 0);
  }

  get pending(): number {
    const value = this.round(this.data.requested - this.assigned);
    return value > EPSILON ? value : 0;
  }

  get totalAvailable(): number {
    return this.rows.filter((r) => !r.withoutStock).reduce((sum, r) => sum + r.available, 0);
  }

  get coversRequested(): boolean {
    return this.pending <= 0;
  }

  // la existencia total no alcanza lo solicitado; en consulta no se conoce el disponible
  get isShortOnStock(): boolean {
    if (this.isReadonly) return false;
    return this.totalAvailable < this.data.requested - EPSILON;
  }

  // solo tiene sentido repartir cuando aún falta cubrir cantidad
  get canFillAutomatically(): boolean {
    return this.pending > 0;
  }

  get canConfirm(): boolean {
    if (this.assigned <= 0) return false;
    return this.coversRequested || this.assigned >= this.totalAvailable - EPSILON;
  }

  onQuantityInput(row: DistributionRow, value: string): void {
    let raw = (value ?? '').replace(/[^0-9.]/g, '');
    const firstDot = raw.indexOf('.');
    if (firstDot !== -1) {
      raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
    }
    let quantity = raw === '' || raw === '.' ? 0 : parseFloat(raw);
    // ningún almacén puede aportar más de lo que tiene; la fila sin respaldo no tiene tope
    if (!row.withoutStock && quantity > row.available) {
      quantity = this.round(row.available);
      raw = this.format(quantity);
    }
    row.quantity = quantity;
    row.quantityText = raw;
    if (this.data.allowsWithoutStock) this.syncWithoutStockRow();
  }

  onQuantityKeydown(e: KeyboardEvent): void {
    const control = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    if (control.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (/^[0-9]$/.test(e.key)) return;
    if (e.key === '.' && !(e.target as HTMLInputElement).value.includes('.')) return;
    e.preventDefault();
  }

  // reparte lo pendiente recorriendo los almacenes en su orden de prioridad,
  // tomando de cada uno lo disponible y pasando el resto al siguiente
  fillAutomatically(): void {
    let pending = this.data.requested;
    for (const row of this.rows) {
      if (row.withoutStock) continue;
      const take = this.round(Math.min(pending, row.available));
      row.quantity = take > 0 ? take : 0;
      row.quantityText = take > 0 ? this.format(take) : '';
      pending = this.round(pending - take);
    }
    if (this.data.allowsWithoutStock) this.syncWithoutStockRow();
  }

  clear(): void {
    this.rows.forEach((r) => {
      r.quantity = 0;
      r.quantityText = '';
    });
    if (this.data.allowsWithoutStock) this.syncWithoutStockRow();
  }

  confirm(): void {
    const allocations: WarehouseAllocation[] = this.rows
      .filter((r) => (r.quantity || 0) > 0)
      .map((r) => ({
        warehouseId: r.warehouseId,
        warehouseCode: r.warehouseCode,
        warehouseName: r.warehouseName,
        quantity: r.quantity,
        withoutStock: r.withoutStock || undefined,
      }));
    this.dialogRef.close(allocations);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // el faltante se surte del almacén principal. La fila aparece solo cuando un reparto
  // real dejó cantidad sin cubrir: si no hay existencia en ningún almacén no hay nada
  // que repartir, y entonces es una venta normal desde el principal
  private syncWithoutStockRow(): void {
    const index = this.rows.findIndex((r) => r.withoutStock);
    const withStock = this.rows.filter((r) => !r.withoutStock);
    const assigned = withStock.reduce((sum, r) => sum + (r.quantity || 0), 0);
    const missing = this.round(this.data.requested - assigned);
    // solo falta si ya no queda existencia que asignar en los demás almacenes
    const remainingStock = this.round(
      withStock.reduce((sum, r) => sum + r.available - (r.quantity || 0), 0),
    );
    const distributed = assigned > EPSILON;
    const shortfall = distributed && missing > EPSILON && remainingStock <= EPSILON ? missing : 0;

    if (shortfall <= 0) {
      if (index >= 0) this.rows.splice(index, 1);
      return;
    }
    const main =
      withStock.find((r) => r.warehouseId === this.data.mainWarehouseId) ?? withStock[0];
    const row: DistributionRow = {
      warehouseId: main?.warehouseId ?? '',
      warehouseCode: main?.warehouseCode ?? '',
      warehouseName: main?.warehouseName ?? '',
      available: 0,
      quantity: shortfall,
      quantityText: this.format(shortfall),
      withoutStock: true,
    };
    if (index >= 0) this.rows.splice(index, 1, row);
    else this.rows.push(row);
  }

  // decimales que admite la unidad de la línea para repartir
  private get decimals(): number {
    if (this.data.acceptsDecimals === false) return 0;
    return Number(this.data.decimalsAllowed ?? 0) || 0;
  }

  // evita que el arrastre binario deje restos como 0.0000001, y acota el reparto a
  // los decimales que admite la unidad de la línea
  private round(value: number): number {
    const corrected = Math.round(value * 1e6) / 1e6;
    if (this.decimals <= 0) return Math.floor(corrected);
    const factor = Math.pow(10, this.decimals);
    return Math.floor(corrected * factor) / factor;
  }

  // el texto se muestra con los decimales que admite la unidad, no con los de la compañía
  private format(value: number): string {
    const text = value.toFixed(this.decimals);
    return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
  }
}
